import { Fragment, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PricingFiltersInput } from "@reina/shared";
import type {
  Brand,
  ComputeResult,
  LineItem,
  LineSource,
  PricingDetail,
  PricingMode,
  SourceMetadata,
  Supplier,
} from "@reina/shared";
import {
  CodeChip,
  Button,
  Dropzone,
  EmptyState,
  Field,
  HoverDetail,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  SupplierAvatar,
  TableWrap,
  useToast,
} from "../../components";
import { useBranches, useBrands, useRateCards, useSuppliers } from "../../api/catalogue";
import {
  useAddLine,
  useComputePricing,
  useDeleteLines,
  useDeletePricing,
  useOverrideLine,
  usePasteBuyList,
  usePricing,
  useProject,
  useSaveFilters,
  useUpdatePricing,
  useUpdateSourceMeta,
  useUploadBuyList,
  sourceFileUrl,
} from "../../api/pricings";
import {
  errorMessage,
  formatConfidence,
  formatDate,
  formatMoney,
} from "../../lib/format";
import { numberOrNull, textOrNull } from "../../lib/form";
import { matchStateLabel, pricingStatusLabel, pricingStatusTone } from "../../lib/tones";

export function PricingDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const detail = usePricing(id);
  const project = useProject(detail.data?.projectId ?? "");
  const suppliers = useSuppliers();
  const brands = useBrands();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const supplierById = useMemo(
    () => new Map((suppliers.data?.items ?? []).map((s) => [s.id, s])),
    [suppliers.data]
  );

  if (detail.isPending) {
    return (
      <Panel>
        <PanelBody>
          <EmptyState title="Loading pricing…" />
        </PanelBody>
      </Panel>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Panel>
        <PanelBody>
          <EmptyState
            title="Could not load pricing"
            hint={detail.error ? errorMessage(detail.error) : undefined}
            action={<Button onClick={() => navigate("/projects")}>Back to projects</Button>}
          />
        </PanelBody>
      </Panel>
    );
  }

  const pricing = detail.data;

  return (
    <div className="stack">
      <div className="breadcrumb">
        <button type="button" onClick={() => navigate("/projects")}>
          Projects
        </button>
        <span>/</span>
        <button type="button" onClick={() => navigate(`/projects/${pricing.projectId}`)}>
          {project.data?.name ?? "Project"}
        </button>
        <span>/</span>
        <span>{pricing.name}</span>
      </div>

      <Panel>
        <PanelHead
          title={pricing.name}
          sub={`run ${formatDate(pricing.runDate)}${pricing.asAtDate ? ` · as at ${formatDate(pricing.asAtDate)}` : " · latest prices"}`}
          actions={
            <>
              <Pill tone={pricingStatusTone[pricing.status]}>
                {pricingStatusLabel[pricing.status]}
              </Pill>
              <Button onClick={() => setEditOpen(true)}>Edit</Button>
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                Delete
              </Button>
            </>
          }
        />
      </Panel>

      <BuyListSection pricing={pricing} />
      <FiltersSection
        key={`${pricing.id}-filters`}
        pricing={pricing}
        suppliers={suppliers.data?.items ?? []}
        brands={brands.data?.items ?? []}
      />
      <ResultsSection pricing={pricing} supplierById={supplierById} />

      {editOpen && <EditPricingModal pricing={pricing} onClose={() => setEditOpen(false)} />}
      {deleteOpen && (
        <DeletePricingModal
          pricing={pricing}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => navigate(`/projects/${pricing.projectId}`)}
        />
      )}
    </div>
  );
}

export function DeletePricingModal({
  pricing,
  onClose,
  onDeleted,
}: {
  pricing: { id: string; projectId: string; name: string };
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const toast = useToast();
  const deletePricing = useDeletePricing();

  const confirm = () =>
    deletePricing.mutate(
      { id: pricing.id, projectId: pricing.projectId },
      {
        onSuccess: () => {
          toast.push({ message: `Pricing "${pricing.name}" deleted`, tone: "ok" });
          onClose();
          onDeleted?.();
        },
        onError: (error) => toast.push({ message: errorMessage(error), tone: "danger" }),
      },
    );

  return (
    <Modal
      open
      title={`Delete "${pricing.name}"?`}
      onClose={onClose}
      footer={
        <div className="actions">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirm} disabled={deletePricing.isPending}>
            {deletePricing.isPending ? "Deleting…" : "Delete pricing"}
          </Button>
        </div>
      }
    >
      <p className="sub">
        This permanently removes the pricing, its buy-list lines, filters and results. The audit
        trail keeps a record. Finalised pricings cannot be deleted.
      </p>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// a. Buy-list: paste/upload lines, review matches, grouped by import source
// ---------------------------------------------------------------------------

const sourceKindTag: Record<LineSource["kind"], string> = {
  upload: "file",
  pasted: "paste",
  manual: "manual",
};

/** Full-width group header for one import (or the legacy/unattributed group). */
function SourceHeaderRow({
  source,
  lineCount,
  pricingId,
  detailsOpen,
  onToggleDetails,
}: {
  source: LineSource | null;
  lineCount: number;
  pricingId: string;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}) {
  const meta = source?.metadata;
  return (
    <tr className="source-row">
      <td colSpan={6}>
        <span className="source-head">
          <span className="kind-tag">{source ? sourceKindTag[source.kind] : "earlier"}</span>
          <strong>{source?.label ?? "Earlier lines"}</strong>
          {meta?.reference && <CodeChip code={meta.reference} title="Document reference" />}
          {meta?.subject && meta.subject !== source?.label && (
            <span className="sub" style={{ margin: 0 }}>
              {meta.subject}
            </span>
          )}
          {meta?.sender && (
            <span className="sub" style={{ margin: 0 }}>
              from {meta.sender}
            </span>
          )}
          {meta?.documentDate && (
            <span className="mono sub" style={{ margin: 0 }}>
              {formatDate(meta.documentDate.slice(0, 10))}
            </span>
          )}
          <span className="sub" style={{ margin: 0 }}>
            {lineCount} line{lineCount === 1 ? "" : "s"}
          </span>
          <span className="source-actions">
            {source?.hasFile && (
              <a
                className="btn mini"
                href={sourceFileUrl(pricingId, source.id)}
                target="_blank"
                rel="noreferrer"
              >
                Open file
              </a>
            )}
            {source && (
              <Button mini variant="ghost" onClick={onToggleDetails}>
                {detailsOpen ? "Hide details" : "Details"}
              </Button>
            )}
          </span>
        </span>
      </td>
    </tr>
  );
}

/** Expandable metadata editor under a source header. */
function SourceMetaRow({ source, pricingId }: { source: LineSource; pricingId: string }) {
  const toast = useToast();
  const save = useUpdateSourceMeta();
  const [meta, setMeta] = useState<SourceMetadata>({ ...source.metadata });
  const set = (key: keyof SourceMetadata, value: string) =>
    setMeta((current) => ({ ...current, [key]: value || null }));

  const fields: Array<{ key: keyof SourceMetadata; label: string; hint?: string }> = [
    { key: "reference", label: "Reference", hint: "PO / order / invoice number." },
    { key: "sender", label: "Source / sender", hint: "Who issued or sent the document." },
    { key: "documentDate", label: "Document date" },
    { key: "subject", label: "Subject / title" },
    { key: "sourceType", label: "Source type" },
    { key: "notes", label: "Notes" },
  ];

  return (
    <tr className="source-meta-row">
      <td colSpan={6}>
        <div className="field-grid three">
          {fields.map(({ key, label, hint }) => (
            <Field key={key} label={label} {...(hint ? { hint } : {})}>
              <input
                className="field"
                value={meta[key] ?? ""}
                onChange={(event) => set(key, event.target.value)}
              />
            </Field>
          ))}
        </div>
        <div className="actions" style={{ marginTop: 8 }}>
          <Button
            mini
            variant="primary"
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                { pricingId, sourceId: source.id, input: meta },
                {
                  onSuccess: () => toast.push({ message: "Source details saved", tone: "ok" }),
                  onError: (error) =>
                    toast.push({ message: errorMessage(error), tone: "danger" }),
                },
              )
            }
          >
            {save.isPending ? "Saving…" : "Save details"}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function BuyListSection({ pricing }: { pricing: PricingDetail }) {
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [reviewLine, setReviewLine] = useState<LineItem | null>(null);
  const [newLine, setNewLine] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const override = useOverrideLine();
  const removeLines = useDeleteLines();
  const addLine = useAddLine();
  const matching = pricing.status === "matching";
  const locked = pricing.status === "finalised" || pricing.status === "archived";
  const readOnly = matching || locked;
  const lines = pricing.lines;
  const [metaOpenFor, setMetaOpenFor] = useState<string | null>(null);

  const onError = (error: unknown) => toast.push({ message: errorMessage(error), tone: "danger" });

  // Group lines by import source: uploads/pastes in arrival order, then any
  // legacy unattributed lines, with manually-added lines as their own section.
  const groups = useMemo(() => {
    const bySource = new Map<string | null, LineItem[]>();
    for (const line of lines) {
      const key = line.sourceId ?? null;
      bySource.set(key, [...(bySource.get(key) ?? []), line]);
    }
    const ordered = [...pricing.sources].sort((a, b) => {
      if ((a.kind === "manual") !== (b.kind === "manual")) return a.kind === "manual" ? 1 : -1;
      return a.createdAt.localeCompare(b.createdAt);
    });
    const result: Array<{ source: LineSource | null; lines: LineItem[] }> = ordered
      .map((source) => ({ source: source as LineSource | null, lines: bySource.get(source.id) ?? [] }))
      .filter((group) => group.lines.length > 0 || group.source?.kind !== "manual");
    const legacy = bySource.get(null) ?? [];
    if (legacy.length) result.push({ source: null, lines: legacy });
    return result;
  }, [lines, pricing.sources]);

  // Selection can only reference lines that still exist.
  const selected = useMemo(
    () => new Set([...selectedIds].filter((id) => lines.some((line) => line.id === id))),
    [selectedIds, lines],
  );
  const allSelected = lines.length > 0 && selected.size === lines.length;

  const toggleLine = (lineId: string) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(lines.map((line) => line.id)));

  const deleteSelected = () =>
    removeLines.mutate(
      { pricingId: pricing.id, lineIds: [...selected] },
      {
        onSuccess: ({ deleted }) => {
          setSelectedIds(new Set());
          toast.push({ message: `${deleted} line${deleted === 1 ? "" : "s"} removed`, tone: "ok" });
        },
        onError,
      },
    );

  const submitNewLine = () => {
    const text = newLine.trim();
    if (!text) return;
    addLine.mutate(
      { pricingId: pricing.id, text },
      {
        onSuccess: () => {
          setNewLine("");
          toast.push({ message: "Line added and matched", tone: "ok" });
        },
        onError,
      },
    );
  };

  const addButton = (
    <Button variant="primary" onClick={() => setAddOpen(true)} disabled={matching}>
      Add lines
    </Button>
  );

  const matchedCandidate = (line: LineItem) =>
    line.matchedProductId
      ? (line.candidateProducts.find((c) => c.productId === line.matchedProductId) ?? null)
      : null;

  /** The source document's own product code for the line, when present. */
  const sourceCode = (line: LineItem): string | null => {
    const fromFields = line.sourceFields?.find((field) =>
      /(code|sku|part|ref)/i.test(field.label),
    )?.value;
    if (fromFields) return fromFields;
    return /\b[A-Z]{2}\d{2}\.\d{4}\b/i.exec(line.rawInput)?.[0]?.toUpperCase() ?? null;
  };

  const MATCH_META: Record<
    LineItem["matchState"],
    { icon: string; tone: string; explain: (line: LineItem) => string }
  > = {
    auto_matched: {
      icon: "⚡",
      tone: "ok",
      explain: (l) =>
        `Matched automatically. The matcher is ${formatConfidence(l.matchConfidence)} confident this catalogue product is what the line asks for. Click the product to review or change it.`,
    },
    confirmed: {
      icon: "✓",
      tone: "ok",
      explain: () =>
        "Confirmed by a person (100% confidence). This match is used as-is when pricing. Click the product to change it.",
    },
    needs_review: {
      icon: "?",
      tone: "warn",
      explain: (l) =>
        `Needs review. Candidates were found but none was certain enough to match automatically${l.matchConfidence ? ` (best ${formatConfidence(l.matchConfidence)})` : ""}. Click the product selector to pick the right one — the line is not priced until matched.`,
    },
    unmatched: {
      icon: "○",
      tone: "neutral",
      explain: () =>
        "No match. Nothing close enough was found in the catalogue. Edit the description to re-match, or pick a product manually via the selector.",
    },
    rejected: {
      icon: "✕",
      tone: "danger",
      explain: () =>
        "Match cleared by a person. The line is excluded from pricing until a product is picked via the selector.",
    },
  };

  return (
    <Panel>
      <PanelHead
        title="Buy-list"
        sub={
          lines.length > 0
            ? `${lines.length} line${lines.length === 1 ? "" : "s"}${matching ? " · matching in the background" : ""}`
            : "paste or upload the customer's shopping list"
        }
        actions={
          <>
            {matching && <Pill tone="warn">Matching…</Pill>}
            {selected.size > 0 && (
              <>
                <span className="sub">{selected.size} selected</span>
                <Button
                  mini
                  variant="danger"
                  onClick={deleteSelected}
                  disabled={readOnly || removeLines.isPending}
                >
                  {removeLines.isPending ? "Deleting…" : "Delete selected"}
                </Button>
              </>
            )}
            {addButton}
          </>
        }
      />
      {lines.length === 0 ? (
        <PanelBody>
          <EmptyState
            title={matching ? "Matching lines…" : "No lines yet"}
            hint={
              matching
                ? "Lines appear as the matcher works through the list."
                : "Paste free-text lines like “20 × 15mm elbow Hep2O” — each line is matched to the catalogue."
            }
            action={matching ? undefined : addButton}
          />
        </PanelBody>
      ) : (
        <TableWrap className="comparison-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                {selected.size > 0 && (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    title={allSelected ? "Deselect all" : "Select all"}
                  />
                )}
              </th>
              <th>#</th>
              <th>Original line</th>
              <th>Qty</th>
              <th style={{ textAlign: "center" }}>Match</th>
              <th>Product</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.source?.id ?? "legacy"}>
                <SourceHeaderRow
                  source={group.source}
                  lineCount={group.lines.length}
                  pricingId={pricing.id}
                  detailsOpen={metaOpenFor === group.source?.id}
                  onToggleDetails={() =>
                    setMetaOpenFor((current) =>
                      group.source && current !== group.source.id ? group.source.id : null,
                    )
                  }
                />
                {group.source && metaOpenFor === group.source.id && (
                  <SourceMetaRow
                    key={`${group.source.id}-meta-${group.source.updatedAt}`}
                    source={group.source}
                    pricingId={pricing.id}
                  />
                )}
                {group.lines.map((line) => (
              <tr
                key={line.id}
                className={line.matchState === "needs_review" ? "variance-row" : undefined}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(line.id)}
                    onChange={() => toggleLine(line.id)}
                    disabled={readOnly}
                  />
                </td>
                <td className="mono">{line.lineNo}</td>
                <td className="mono">
                  <span className="result-cell">
                    {sourceCode(line) && (
                      <CodeChip code={sourceCode(line)} dim title="Code from source document" />
                    )}
                    <HoverDetail
                      summary={
                        line.rawInput.length > 40
                          ? `${line.rawInput.slice(0, 40)}…`
                          : line.rawInput
                      }
                    >
                      <strong>{line.rawInput}</strong>
                      <span style={{ display: "block", marginTop: 8 }}>
                        <span style={{ display: "block" }}>
                          <span className="label">Parsed as:</span> {line.requestedQty ?? 1} ×{" "}
                          {line.parsedDescription ?? "—"}
                          {line.requestedUom ? ` (${line.requestedUom})` : ""}
                        </span>
                        {line.sourceFields?.map((field) => (
                          <span key={`${field.label}:${field.value}`} style={{ display: "block" }}>
                            <span className="label">{field.label}:</span> {field.value}
                          </span>
                        ))}
                      </span>
                    </HoverDetail>
                  </span>
                </td>
                <td className="mono">
                  <input
                    key={`${line.id}-qty-${line.requestedQty}`}
                    className="qty-input"
                    type="number"
                    min={0.001}
                    step="any"
                    defaultValue={line.requestedQty ?? 1}
                    disabled={readOnly}
                    onBlur={(event) => {
                      const qty = Number(event.target.value);
                      if (Number.isFinite(qty) && qty > 0 && qty !== line.requestedQty) {
                        override.mutate(
                          { pricingId: pricing.id, lineId: line.id, input: { requestedQty: qty } },
                          { onError },
                        );
                      }
                    }}
                  />
                  {line.requestedUom ? ` ${line.requestedUom}` : ""}
                </td>
                <td style={{ textAlign: "center" }}>
                  <HoverDetail
                    summary={
                      <span className={`match-icon ${MATCH_META[line.matchState].tone}`}>
                        {MATCH_META[line.matchState].icon}
                      </span>
                    }
                  >
                    <strong>{matchStateLabel[line.matchState]}</strong>
                    {"\n"}
                    {MATCH_META[line.matchState].explain(line)}
                  </HoverDetail>
                </td>
                <td>
                  {(() => {
                    const candidate = matchedCandidate(line);
                    return (
                      <button
                        type="button"
                        className="select-like"
                        disabled={locked}
                        title="Open the match review to pick or change the product"
                        onClick={() => setReviewLine(line)}
                      >
                        {candidate || line.matchedProductId ? (
                          <>
                            <CodeChip code={candidate?.iqCode ?? null} title="Supplier code" />
                            <span>
                              {candidate?.description ?? line.matchedProductId?.slice(0, 8)}
                            </span>
                          </>
                        ) : (
                          <span className="placeholder">Choose product…</span>
                        )}
                        <span className="caret">▾</span>
                      </button>
                    );
                  })()}
                </td>
              </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </TableWrap>
      )}
      {!readOnly && (
        <PanelBody>
          <div className="actions">
            <input
              className="field"
              style={{ flex: 1 }}
              placeholder='Add a line, e.g. "12 x 15mm Hep2O elbow" — Enter to add'
              value={newLine}
              onChange={(event) => setNewLine(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submitNewLine();
              }}
            />
            <Button
              mini
              variant="primary"
              onClick={submitNewLine}
              disabled={!newLine.trim() || addLine.isPending}
            >
              {addLine.isPending ? "Adding…" : "Add line"}
            </Button>
          </div>
        </PanelBody>
      )}
      {addOpen && <AddLinesModal pricingId={pricing.id} onClose={() => setAddOpen(false)} />}
      {reviewLine && (
        <CandidatesModal
          key={reviewLine.id}
          pricingId={pricing.id}
          line={reviewLine}
          onClose={() => setReviewLine(null)}
        />
      )}
    </Panel>
  );
}

function AddLinesModal({ pricingId, onClose }: { pricingId: string; onClose: () => void }) {
  const toast = useToast();
  const paste = usePasteBuyList();
  const upload = useUploadBuyList();
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const busy = paste.isPending || upload.isPending;

  const submit = async () => {
    try {
      if (file) await upload.mutateAsync({ id: pricingId, file });
      else await paste.mutateAsync({ id: pricingId, text });
      toast.push({ message: "Import queued — parsing and matching…", tone: "ok" });
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <Modal
      open
      title="Add buy-list lines"
      sub="paste free text, or drop a spreadsheet / PDF (invoice, order confirmation, PO)"
      onClose={onClose}
      footer={
        <>
          <span className="sub">Matching runs in the background — the table updates live.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={busy || (!file && !text.trim())}
            >
              {busy ? "Submitting…" : "Add lines"}
            </Button>
          </div>
        </>
      }
    >
      <Field label="Paste lines" hint="One item per line, quantities included.">
        <textarea
          className="field"
          rows={8}
          autoFocus
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={"20 x 15mm elbow Hep2O\n10 x 22mm coupler\n6 x K2 600x1200 radiator"}
          disabled={file !== null}
        />
      </Field>
      <Dropzone
        accept=".txt,.csv,.xlsx,.xlsm,.xlsb,.xls,.ods,.pdf,.docx,.odt,.png,.jpg,.jpeg,.webp,.eml,.msg"
        state={file ? "ready" : "idle"}
        onFiles={(files) => setFile(files[0] ?? null)}
      >
        {file ? (
          <span className="result-cell">
            <span className="file-chip">{file.name}</span>
            <Button
              mini
              onClick={(event) => {
                event.stopPropagation();
                setFile(null);
              }}
            >
              Remove
            </Button>
          </span>
        ) : (
          <>
            <strong>…or drag the buy-list file here</strong>
            <span className="sub">.txt or .csv — used instead of the paste box</span>
          </>
        )}
      </Dropzone>
    </Modal>
  );
}

function CandidatesModal({
  pricingId,
  line,
  onClose,
}: {
  pricingId: string;
  line: LineItem;
  onClose: () => void;
}) {
  const toast = useToast();
  const override = useOverrideLine();
  const [description, setDescription] = useState(line.parsedDescription ?? "");

  const rematch = async () => {
    const value = description.trim();
    if (!value || value === line.parsedDescription) return;
    try {
      await override.mutateAsync({ pricingId, lineId: line.id, input: { description: value } });
      toast.push({ message: "Line re-matched with the edited description", tone: "ok" });
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  const pick = async (matchedProductId: string | null) => {
    try {
      await override.mutateAsync({ pricingId, lineId: line.id, input: { matchedProductId } });
      toast.push({
        message: matchedProductId ? "Match confirmed" : "Match cleared",
        tone: "ok",
      });
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  const candidates = [...line.candidateProducts].sort((a, b) => b.score - a.score);

  return (
    <Modal
      open
      wide
      resizable
      title="Review match"
      sub={line.rawInput}
      onClose={onClose}
      footer={
        <>
          <span className="sub">Picking a product confirms the line at 100% confidence.</span>
          <div className="actions">
            <Button
              variant="danger"
              onClick={() => void pick(null)}
              disabled={override.isPending || !line.matchedProductId}
            >
              Clear match
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      }
    >
      <Field
        label="Parsed description"
        hint="Edit and re-match when the parsed text misread the source line."
      >
        <div className="actions">
          <input
            className="field"
            style={{ flex: 1 }}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void rematch();
            }}
          />
          <Button
            mini
            disabled={override.isPending || !description.trim() || description.trim() === line.parsedDescription}
            onClick={() => void rematch()}
          >
            Re-match
          </Button>
        </div>
      </Field>
      {candidates.length === 0 ? (
        <EmptyState
          title="No candidates"
          hint="The matcher found nothing close — check the catalogue or reword the line."
        />
      ) : (
        <div className="line-list">
          {candidates.map((candidate) => {
            const isCurrent = candidate.productId === line.matchedProductId;
            return (
              <div key={candidate.productId} className={isCurrent ? "line-row ok" : "line-row"}>
                <span>
                  <span className="result-cell">
                    <CodeChip code={candidate.iqCode ?? null} title="Supplier code" />
                    <strong>{candidate.description}</strong>
                  </span>
                  <span className="sub">
                    {candidate.brandName ? `${candidate.brandName} · ` : ""}score{" "}
                    {candidate.score.toFixed(2)}
                  </span>
                </span>
                <span className="actions">
                  {isCurrent && <Pill tone="ok">Current</Pill>}
                  <Button
                    mini
                    variant="primary"
                    disabled={override.isPending || isCurrent}
                    onClick={() => void pick(candidate.productId)}
                  >
                    Pick
                  </Button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// b. Filters
// ---------------------------------------------------------------------------

function FiltersSection({
  pricing,
  suppliers,
  brands,
}: {
  pricing: PricingDetail;
  suppliers: Supplier[];
  brands: Brand[];
}) {
  const toast = useToast();
  const save = useSaveFilters();
  const rateCards = useRateCards();
  const [filters, setFilters] = useState<PricingFiltersInput>(
    () => pricing.filterSettings ?? PricingFiltersInput.parse({ mode: "single_supplier" })
  );

  // Branch restriction (FR13) needs a supplier context: the forced supplier,
  // or a single included supplier.
  const branchSupplierId =
    filters.forcedSupplierId ??
    (filters.includeSupplierIds.length === 1 ? (filters.includeSupplierIds[0] ?? null) : null);
  const branches = useBranches(branchSupplierId);

  const set = <K extends keyof PricingFiltersInput>(key: K, value: PricingFiltersInput[K]) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const multiValue = (event: ChangeEvent<HTMLSelectElement>): string[] =>
    Array.from(event.target.selectedOptions, (option) => option.value);

  const onSave = async () => {
    try {
      await save.mutateAsync({ id: pricing.id, input: filters });
      toast.push({ message: "Filters saved", tone: "ok" });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  const modeButton = (mode: PricingMode, label: string) => (
    <button
      type="button"
      className={filters.mode === mode ? "active" : undefined}
      onClick={() => set("mode", mode)}
    >
      {label}
    </button>
  );

  return (
    <Panel>
      <PanelHead
        title="Filters"
        sub="strategy + constraints applied at compute time"
        actions={
          <Button variant="primary" onClick={() => void onSave()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save filters"}
          </Button>
        }
      />
      <PanelBody className="stack">
        <div className="kit-tabs" style={{ marginBottom: 0 }}>
          {modeButton("single_supplier", "Whole batch → one supplier")}
          {modeButton("cheapest_per_line", "Cheapest per line")}
        </div>

        <FieldGrid columns={3}>
          {filters.mode === "single_supplier" && (
            <Field label="Forced supplier" hint="Leave empty to rank all suppliers.">
              <select
                className="field"
                value={filters.forcedSupplierId ?? ""}
                onChange={(event) => set("forcedSupplierId", event.target.value || null)}
              >
                <option value="">No — rank all</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="As at" hint="Set on the pricing itself (Edit).">
            <input
              className="field"
              readOnly
              value={pricing.asAtDate ? formatDate(pricing.asAtDate) : "Latest prices"}
            />
          </Field>
          <Field label="Rate card" hint="Restrict prices to one terms code.">
            <select
              className="field"
              value={filters.rateCardId ?? ""}
              onChange={(event) => set("rateCardId", event.target.value || null)}
            >
              <option value="">Any rate card</option>
              {(rateCards.data?.items ?? []).map((rateCard) => (
                <option key={rateCard.id} value={rateCard.id}>
                  {rateCard.termsCode} — {rateCard.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Branch"
            hint={
              branchSupplierId
                ? "Restrict prices to one branch."
                : "Pick a forced supplier (or exactly one included supplier) to enable."
            }
          >
            <select
              className="field"
              value={filters.branchId ?? ""}
              onChange={(event) => set("branchId", event.target.value || null)}
              disabled={!branchSupplierId}
            >
              <option value="">Any branch</option>
              {(branches.data?.items ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field label="Include suppliers" hint="Empty = all suppliers considered.">
            <select
              multiple
              size={5}
              className="field"
              value={filters.includeSupplierIds}
              onChange={(event) => set("includeSupplierIds", multiValue(event))}
            >
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Exclude suppliers">
            <select
              multiple
              size={5}
              className="field"
              value={filters.excludeSupplierIds}
              onChange={(event) => set("excludeSupplierIds", multiValue(event))}
            >
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Include brands" hint="Empty = all brands considered.">
            <select
              multiple
              size={5}
              className="field"
              value={filters.includeBrandIds}
              onChange={(event) => set("includeBrandIds", multiValue(event))}
            >
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Exclude brands">
            <select
              multiple
              size={5}
              className="field"
              value={filters.excludeBrandIds}
              onChange={(event) => set("excludeBrandIds", multiValue(event))}
            >
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </Field>
        </FieldGrid>

        <FieldGrid columns={3}>
          <Field label="Min match confidence" hint="0–1; lines below are not priced.">
            <input
              className="field"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={filters.minMatchConfidence}
              onChange={(event) => set("minMatchConfidence", numberOrNull(event.target.value) ?? 0)}
            />
          </Field>
          <Field label="Margin uplift %" hint="Applied on top of nett.">
            <input
              className="field"
              type="number"
              min={0}
              step={0.5}
              value={filters.marginUpliftPct}
              onChange={(event) => set("marginUpliftPct", numberOrNull(event.target.value) ?? 0)}
            />
          </Field>
          <Field label="Max price age (days)" hint="Empty = no age limit.">
            <input
              className="field"
              type="number"
              min={1}
              step={1}
              value={filters.maxPriceAgeDays ?? ""}
              onChange={(event) => set("maxPriceAgeDays", numberOrNull(event.target.value))}
            />
          </Field>
        </FieldGrid>

        <div className="actions" style={{ justifyContent: "flex-start" }}>
          <label className="result-cell">
            <input
              type="checkbox"
              checked={filters.perUnitNormalisation}
              onChange={(event) => set("perUnitNormalisation", event.target.checked)}
            />
            Per-unit normalisation
          </label>
          <label className="result-cell">
            <input
              type="checkbox"
              checked={filters.applyDiscounts}
              onChange={(event) => set("applyDiscounts", event.target.checked)}
            />
            Apply discounts
          </label>
          <label className="result-cell">
            <input
              type="checkbox"
              checked={filters.allowBrandSubstitution}
              onChange={(event) => set("allowBrandSubstitution", event.target.checked)}
            />
            Brand substitution
          </label>
          <label className="result-cell">
            <input
              type="checkbox"
              checked={filters.includeUnmatched}
              onChange={(event) => set("includeUnmatched", event.target.checked)}
            />
            Count unmatched as uncovered
          </label>
        </div>
      </PanelBody>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// c. Results: both strategies + delta
// ---------------------------------------------------------------------------

function ResultsSection({
  pricing,
  supplierById,
}: {
  pricing: PricingDetail;
  supplierById: Map<string, Supplier>;
}) {
  const toast = useToast();
  const compute = useComputePricing();
  const results: ComputeResult | null = pricing.results;

  const onCompute = async () => {
    try {
      await compute.mutateAsync(pricing.id);
      toast.push({ message: "Computed both strategies", tone: "ok" });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  const computeButton = (
    <Button
      variant="orange"
      onClick={() => void onCompute()}
      disabled={compute.isPending || pricing.lines.length === 0 || pricing.status === "matching"}
    >
      {compute.isPending ? "Computing…" : "Compute"}
    </Button>
  );

  const lineById = new Map(pricing.lines.map((line) => [line.id, line]));
  const supplierName = (id: string) => supplierById.get(id)?.name ?? id.slice(0, 8);
  const supplierLogo = (id: string) => supplierById.get(id)?.logo ?? null;

  // Best single supplier = lowest total among those covering the most lines.
  const bestSingle = results
    ? [...results.singleSupplier].sort(
        (a, b) => b.coveredLines - a.coveredLines || a.total - b.total
      )[0] ?? null
    : null;
  const rankedSingles = results
    ? [...results.singleSupplier].sort((a, b) => a.total - b.total)
    : [];
  const delta = results && bestSingle ? bestSingle.total - results.cheapestPerLine.total : null;

  return (
    <Panel className={results ? "results-in" : undefined}>
      <PanelHead
        title="Results"
        sub="both strategies computed for comparison — single supplier vs cheapest per line"
        actions={computeButton}
      />
      {!results ? (
        <PanelBody>
          <EmptyState
            title="Not computed yet"
            hint={
              pricing.lines.length === 0
                ? "Add buy-list lines first, then compute."
                : "Hit Compute to price the buy-list under the saved filters."
            }
            action={pricing.lines.length > 0 ? computeButton : undefined}
          />
        </PanelBody>
      ) : (
        <>
          <PanelBody className="stack">
            <div className="totals-ribbon">
              <div>
                <span>Best single supplier</span>
                <strong>{bestSingle ? supplierName(bestSingle.supplierId) : "—"}</strong>
              </div>
              <div>
                <span>Single-supplier total</span>
                <strong>{formatMoney(bestSingle?.total ?? null)}</strong>
              </div>
              <div>
                <span>Cheapest per line</span>
                <strong className="good">{formatMoney(results.cheapestPerLine.total)}</strong>
              </div>
              <div>
                <span>Delta (single − mixed)</span>
                <strong className={delta != null && delta > 0 ? "warn" : "good"}>
                  {delta != null ? formatMoney(delta) : "—"}
                </strong>
              </div>
            </div>
            {delta != null && (
              <span className={delta > 0.005 ? "delta up" : "delta flat"}>
                {delta > 0.005
                  ? `Splitting the order saves ${formatMoney(delta)} vs the best single supplier`
                  : "Best single supplier matches the cheapest-per-line total"}
              </span>
            )}
          </PanelBody>

          {rankedSingles.length === 0 ? (
            <PanelBody>
              <EmptyState
                title="No supplier can price this list"
                hint="Check a price list is published and this pricing has matched lines (see About), then loosen the filters (brands, suppliers, price age) and recompute."
              />
            </PanelBody>
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Supplier</th>
                  <th>Total</th>
                  <th>Covered</th>
                  <th>Uncovered</th>
                  <th aria-label="Best" />
                </tr>
              </thead>
              <tbody>
                {rankedSingles.map((result, index) => (
                  <tr key={result.supplierId}>
                    <td className="mono">{index + 1}</td>
                    <td>
                      <span className="result-cell">
                        <SupplierAvatar
                          name={result.supplierName || supplierName(result.supplierId)}
                          logo={supplierLogo(result.supplierId)}
                        />
                        <strong>{result.supplierName || supplierName(result.supplierId)}</strong>
                      </span>
                    </td>
                    <td className="money">{formatMoney(result.total)}</td>
                    <td className="mono">{result.coveredLines}</td>
                    <td className="mono">
                      {result.uncoveredLines > 0 ? (
                        <Pill tone="warn">{result.uncoveredLines}</Pill>
                      ) : (
                        0
                      )}
                    </td>
                    <td>
                      {bestSingle?.supplierId === result.supplierId && <Pill tone="ok">Best</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}

          <PanelHead
            title="Cheapest per line"
            sub={`mixed-basket breakdown · total ${formatMoney(results.cheapestPerLine.total)}`}
          />
          {results.cheapestPerLine.lines.length === 0 ? (
            <PanelBody>
              <EmptyState title="No lines priced" hint="No current prices matched under the filters." />
            </PanelBody>
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Line</th>
                  <th>Qty</th>
                  <th>Supplier</th>
                  <th>Nett</th>
                  <th>Line total</th>
                </tr>
              </thead>
              <tbody>
                {results.cheapestPerLine.lines.map((line) => {
                  const source = lineById.get(line.lineId);
                  return (
                    <tr key={line.lineId}>
                      <td className="mono">{source?.lineNo ?? "—"}</td>
                      <td>{source ? source.parsedDescription ?? source.rawInput : line.lineId.slice(0, 8)}</td>
                      <td className="mono">{source?.requestedQty ?? "—"}</td>
                      <td>
                        <span className="result-cell">
                          <SupplierAvatar
                            name={supplierName(line.supplierId)}
                            logo={supplierLogo(line.supplierId)}
                            size={28}
                          />
                          {supplierName(line.supplierId)}
                        </span>
                      </td>
                      <td className="money">{formatMoney(line.nett)}</td>
                      <td className="money">{formatMoney(line.lineTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </TableWrap>
          )}
        </>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Edit pricing modal (rename / redate / status)
// ---------------------------------------------------------------------------

function EditPricingModal({ pricing, onClose }: { pricing: PricingDetail; onClose: () => void }) {
  const toast = useToast();
  const update = useUpdatePricing();
  const [name, setName] = useState(pricing.name);
  const [runDate, setRunDate] = useState(pricing.runDate);
  const [asAtDate, setAsAtDate] = useState(pricing.asAtDate ?? "");
  const [status, setStatus] = useState(pricing.status);

  const submit = async () => {
    if (!name.trim() || !runDate) {
      toast.push({ message: "Name and run date are required", tone: "warn" });
      return;
    }
    try {
      await update.mutateAsync({
        id: pricing.id,
        input: {
          name: name.trim(),
          runDate,
          asAtDate: textOrNull(asAtDate),
          status,
        },
      });
      toast.push({ message: "Pricing updated", tone: "ok" });
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <Modal
      open
      title="Edit pricing"
      sub={pricing.name}
      onClose={onClose}
      footer={
        <>
          <span className="sub">Finalise when the customer quote goes out.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </>
      }
    >
      <Field label="Name">
        <input className="field" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <FieldGrid columns={3}>
        <Field label="Run date">
          <input
            className="field"
            type="date"
            value={runDate}
            onChange={(e) => setRunDate(e.target.value)}
          />
        </Field>
        <Field label="As-at date" hint="Empty = latest prices.">
          <input
            className="field"
            type="date"
            value={asAtDate}
            onChange={(e) => setAsAtDate(e.target.value)}
          />
        </Field>
        <Field label="Status">
          <select
            className="field"
            value={status}
            onChange={(e) => setStatus(e.target.value as PricingDetail["status"])}
          >
            {(["draft", "ready", "finalised", "archived"] as const).map((value) => (
              <option key={value} value={value}>
                {pricingStatusLabel[value]}
              </option>
            ))}
            {status === "matching" && <option value="matching">Matching…</option>}
          </select>
        </Field>
      </FieldGrid>
    </Modal>
  );
}
